import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfBadge, EmptyState, Metric, PageHeader, StateBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { toast } from "sonner";

const NEXT = { draft: "in_progress", in_progress: "submitted", submitted: "validated", validated: "published" };
const NEXT_LABEL = { in_progress: "Open for response", submitted: "Submit cycle", validated: "Validate cycle", published: "Publish cycle" };

export default function CycleDetail() {
  const { cycleId } = useParams();
  const { tenantId, can, user, access } = useAuth();
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [users, setUsers] = useState([]);
  const [assignee, setAssignee] = useState("");

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await api.get(`/tenants/${tenantId}/panchatanthra/cycles/${cycleId}`);
      setData(res.data);
      const p = await api.get(`/tenants/${tenantId}/panchatanthra/cycles/${cycleId}/preview-score`);
      setPreview(p.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [tenantId, cycleId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!tenantId || !can("assign")) return;
    api.get(`/tenants/${tenantId}/grants`).then(({ data }) => setUsers(data.filter((g) => g.user))).catch(() => {});
  }, [tenantId, can]);

  if (!data) return <EmptyState testid="cycle-loading" title="Loading cycle" message="Fetching assessment workspace…" />;

  const { cycle, version, assignments, responses, snapshots } = data;
  const myResponses = Object.fromEntries(
    responses.filter((r) => r.respondent_id === user?.id).map((r) => [r.question_code, r])
  );
  const options = version?.answer_options || [];
  const allQuestions = (version?.elements || []).flatMap((el) =>
    (el.sub_elements || []).flatMap((s) => s.questions.map((q) => ({ ...q, element_code: el.code, sub_element_code: s.code, element_name: el.name })))
  );
  const answered = allQuestions.filter((q) => myResponses[q.code]).length;
  const latestSnapshot = snapshots[0];

  const answer = async (q, value, label) => {
    try {
      await api.put(`/tenants/${tenantId}/panchatanthra/cycles/${cycleId}/responses`, {
        question_code: q.code,
        element_code: q.element_code,
        sub_element_code: q.sub_element_code,
        option_value: value,
        option_label: label,
        na: value === null,
        notes: myResponses[q.code]?.notes || null,
      });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveNote = async (q, notes) => {
    const existing = myResponses[q.code];
    await api
      .put(`/tenants/${tenantId}/panchatanthra/cycles/${cycleId}/responses`, {
        question_code: q.code,
        element_code: q.element_code,
        sub_element_code: q.sub_element_code,
        option_value: existing?.option_value ?? null,
        option_label: existing?.option_label ?? null,
        na: existing?.na ?? false,
        notes,
      })
      .catch((e) => toast.error(errMsg(e)));
  };

  const transition = async (status) => {
    try {
      await api.post(`/tenants/${tenantId}/panchatanthra/cycles/${cycleId}/transition`, { status });
      toast.success(`Cycle moved to ${status.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const chartData = (latestSnapshot?.elements || preview?.elements || []).map((el) => ({
    name: el.code,
    Current: el.score ?? 0,
    Target: el.target ?? 0,
  }));

  return (
    <div className="space-y-10">
      <PageHeader
        testid="cycle-detail-header"
        eyebrow={`Panchatanthra · version ${cycle.version_label}`}
        title={cycle.name}
        description={`${cycle.period_label || "—"} · scores are frozen into immutable snapshots on publication.`}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <ConfBadge level={cycle.confidentiality} />
            <StateBadge state={cycle.status} />
            {NEXT[cycle.status] && (
              <Button
                data-testid="cycle-transition-button"
                className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]"
                onClick={() => transition(NEXT[cycle.status])}
              >
                {NEXT_LABEL[NEXT[cycle.status]]}
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4 md:gap-6">
        <Metric testid="cycle-metric-progress" label="My progress" value={`${answered}/${allQuestions.length}`} hint="Questions answered" />
        <Metric testid="cycle-metric-respondents" label="Respondents" value={assignments.length} hint={`${assignments.filter((a) => a.status === "submitted").length} submitted`} />
        <Metric testid="cycle-metric-draft-score" label="Draft score" value={preview?.overall_score ?? "—"} hint={preview?.maturity_band} />
        <Metric
          testid="cycle-metric-published-score"
          label="Published score"
          value={latestSnapshot?.overall_score ?? "—"}
          hint={latestSnapshot ? `${latestSnapshot.maturity_band} · immutable` : "Not published"}
          tone="success"
        />
      </div>

      <Tabs defaultValue="respond">
        <TabsList className="rounded-none">
          <TabsTrigger data-testid="tab-respond" value="respond" className="rounded-none text-xs uppercase tracking-[0.15em]">Respond</TabsTrigger>
          <TabsTrigger data-testid="tab-validate" value="validate" className="rounded-none text-xs uppercase tracking-[0.15em]">Validation</TabsTrigger>
          <TabsTrigger data-testid="tab-score" value="score" className="rounded-none text-xs uppercase tracking-[0.15em]">Score & drilldown</TabsTrigger>
          <TabsTrigger data-testid="tab-assign" value="assign" className="rounded-none text-xs uppercase tracking-[0.15em]">Respondents</TabsTrigger>
        </TabsList>

        <TabsContent value="respond" className="mt-8 space-y-8">
          <Progress value={(answered / Math.max(allQuestions.length, 1)) * 100} className="h-1" />
          {(version?.elements || []).map((el) => (
            <div key={el.code} className="panel" data-testid={`element-block-${el.code}`}>
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <h3 className="font-display text-xl">{el.name}</h3>
                <span className="label-caps">weight {el.weight}</span>
              </div>
              {(el.sub_elements || []).map((sub) => (
                <div key={sub.code} className="border-b border-border last:border-0">
                  <p className="label-caps px-5 pt-5">{sub.name}</p>
                  {sub.questions.map((q) => {
                    const r = myResponses[q.code];
                    return (
                      <div key={q.code} className="px-5 py-5" data-testid={`question-${q.code}`}>
                        <p className="text-sm">{q.text}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {(q.options?.length ? q.options : options).map((o) => (
                            <button
                              key={o.value}
                              data-testid={`answer-${q.code}-${o.value}`}
                              disabled={!can("edit") || !["draft", "in_progress"].includes(cycle.status)}
                              onClick={() => answer(q, o.value, o.label)}
                              className={`border px-3 py-1.5 text-xs transition-colors ${
                                r?.option_value === o.value
                                  ? "border-foreground bg-foreground text-background"
                                  : "border-border hover:border-foreground/60"
                              } disabled:opacity-40`}
                            >
                              <span className="font-mono tabular-nums">{o.value}</span> · {o.label}
                            </button>
                          ))}
                          <button
                            data-testid={`answer-${q.code}-na`}
                            disabled={!can("edit") || !["draft", "in_progress"].includes(cycle.status)}
                            onClick={() => answer(q, null, "Not applicable")}
                            className={`border px-3 py-1.5 text-xs transition-colors ${
                              r?.na ? "border-foreground bg-muted" : "border-border hover:border-foreground/60"
                            } disabled:opacity-40`}
                          >
                            N/A
                          </button>
                        </div>
                        <Textarea
                          data-testid={`notes-${q.code}`}
                          defaultValue={r?.notes || ""}
                          onBlur={(e) => saveNote(q, e.target.value)}
                          placeholder="Evidence or rationale (optional)"
                          className="mt-3 rounded-none text-xs"
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
          {can("submit") && (
            <Button
              data-testid="submit-responses-button"
              className="h-10 rounded-none text-[10px] uppercase tracking-[0.18em]"
              onClick={async () => {
                try {
                  await api.post(`/tenants/${tenantId}/panchatanthra/cycles/${cycleId}/responses/submit`);
                  toast.success("Responses submitted for validation");
                  load();
                } catch (e) {
                  toast.error(errMsg(e));
                }
              }}
            >
              Submit my responses
            </Button>
          )}
        </TabsContent>

        <TabsContent value="validate" className="mt-8">
          <div className="panel divide-y divide-border" data-testid="validation-list">
            {responses.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
                <div className="min-w-0">
                  <p className="text-sm">{r.question_code} · {r.element_code}</p>
                  <p className="label-caps mt-1">{r.option_label || (r.na ? "N/A" : "unanswered")} {r.notes ? `· ${r.notes.slice(0, 60)}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm tabular-nums">{r.option_value ?? "—"}</span>
                  <StateBadge state={r.status} />
                  {can("validate") && r.status !== "validated" && (
                    <>
                      <Button
                        data-testid={`validate-${r.id}`}
                        size="sm"
                        variant="outline"
                        className="rounded-none text-[10px] uppercase tracking-[0.15em]"
                        onClick={async () => {
                          await api.post(`/tenants/${tenantId}/panchatanthra/cycles/${cycleId}/responses/${r.id}/validate`, { decision: "validated" });
                          load();
                        }}
                      >
                        Validate
                      </Button>
                      <Button
                        data-testid={`reject-${r.id}`}
                        size="sm"
                        variant="ghost"
                        className="rounded-none text-[10px] uppercase tracking-[0.15em] text-destructive"
                        onClick={async () => {
                          await api.post(`/tenants/${tenantId}/panchatanthra/cycles/${cycleId}/responses/${r.id}/validate`, { decision: "rejected" });
                          load();
                        }}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {responses.length === 0 && <EmptyState testid="validation-empty" title="No responses" message="Nothing has been captured yet." />}
          </div>
        </TabsContent>

        <TabsContent value="score" className="mt-8 space-y-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              {latestSnapshot
                ? `Published snapshot on version ${latestSnapshot.version_label} — immutable.`
                : "Showing draft computation. Publish a snapshot to freeze the score."}
            </p>
            {can("approve") && (
              <Button
                data-testid="publish-snapshot-button"
                className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]"
                onClick={async () => {
                  try {
                    await api.post(`/tenants/${tenantId}/panchatanthra/cycles/${cycleId}/snapshots`);
                    toast.success("Score snapshot published");
                    load();
                  } catch (e) {
                    toast.error(errMsg(e));
                  }
                }}
              >
                Publish score snapshot
              </Button>
            )}
          </div>

          <div className="panel p-6" data-testid="score-chart">
            <p className="label-caps mb-6">Current vs target by element</p>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis domain={[0, 5]} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid hsl(var(--border))" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Current" fill="hsl(var(--foreground))" />
                  <Bar dataKey="Target" fill="hsl(var(--muted-foreground))" fillOpacity={0.4} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="panel divide-y divide-border" data-testid="score-drilldown">
            {(latestSnapshot?.elements || preview?.elements || []).map((el, idx) => (
              <div key={`${el.code}-${idx}`} className="px-5 py-4">
                <div className="flex items-center justify-between">
                  <p className="font-display text-lg">{el.name}</p>
                  <p className="font-mono text-lg tabular-nums">
                    {el.score ?? "—"} <span className="text-xs text-muted-foreground">/ target {el.target ?? "—"}</span>
                  </p>
                </div>
                <div className="mt-3 space-y-1">
                  {(el.sub_elements || []).map((s, sidx) => (
                    <div key={`${s.code}-${sidx}`} className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{s.name}</span>
                      <span className="font-mono tabular-nums">{s.score ?? "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="assign" className="mt-8 space-y-6">
          {can("assign") && (
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[16rem]">
                <p className="label-caps mb-2">Add respondent</p>
                <Select value={assignee} onValueChange={setAssignee}>
                  <SelectTrigger data-testid="assignee-select" className="rounded-none"><SelectValue placeholder="Select engagement member" /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {users.map((g) => (
                      <SelectItem key={g.id} value={g.user_id}>{g.user?.name} · {g.role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                data-testid="add-assignment-button"
                className="h-10 rounded-none text-[10px] uppercase tracking-[0.18em]"
                disabled={!assignee}
                onClick={async () => {
                  try {
                    await api.post(`/tenants/${tenantId}/panchatanthra/cycles/${cycleId}/assignments`, { user_id: assignee, element_codes: [] });
                    toast.success("Respondent assigned and notified");
                    setAssignee("");
                    load();
                  } catch (e) {
                    toast.error(errMsg(e));
                  }
                }}
              >
                Assign
              </Button>
            </div>
          )}
          <div className="panel divide-y divide-border" data-testid="assignments-list">
            {assignments.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-4 px-5 py-4">
                <div>
                  <p className="text-sm">{a.user?.name || a.user_id}</p>
                  <p className="label-caps mt-1">{a.user?.title || a.user?.email}</p>
                </div>
                <StateBadge state={a.status} />
              </div>
            ))}
            {assignments.length === 0 && <EmptyState testid="assignments-empty" title="No respondents" message="Assign contributors to open this cycle for response." />}
          </div>
          <p className="text-xs text-muted-foreground">Your clearance: {["none", "General", "Sensitive", "Restricted"][access?.confidentiality_max || 0]}</p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
